// src/pages/projects/fitness/edit-workouts.tsx
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../../firebase";
import { useAuth } from "../../../components/authContext";
import StyledButton from "../../components/button";
import EditWorkoutRow from "../components/editworkoutrow";

interface LiftSet {
  reps: number;
  weight: number;
  rpe: number;
}

interface RunSet {
  distance: number;
  pace: number;
  time: number;
}

interface WorkoutExercise {
  name: string;
  mode: "lift" | "run";
  sets: (LiftSet | RunSet)[];
}

interface WorkoutLog {
  id: string;
  createdAt: { seconds: number };
  exercises: WorkoutExercise[];
}

const EditWorkoutHistory: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<WorkoutLog[]>([]);
  const [history, setHistory] = useState<WorkoutLog[][]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      const q = query(
        collection(db, `users/${user.uid}/workouts`),
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(q);
      const items = snapshot.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<WorkoutLog, "id">),
      }));
      setLogs(items);
    };
    fetch();
  }, [user]);

  const updateWorkout = async (updated: WorkoutLog) => {
    if (!user) return;
    const ref = doc(db, `users/${user.uid}/workouts`, updated.id);
    await updateDoc(ref, { exercises: updated.exercises });
  };

  const saveAllLogs = async () => {
    if (!user) return;
    try {
      await Promise.all(
        logs.map((log) =>
          updateDoc(doc(db, `users/${user.uid}/workouts`, log.id), {
            exercises: log.exercises,
          })
        )
      );
    } catch (error) {
      console.error("Save failed:", error);
      alert("Failed to save changes. Check console for details.");
    }
  };

  const handleDeleteWorkout = async (index: number) => {
    if (!user) return;
    const logToDelete = logs[index];
    await deleteDoc(doc(db, `users/${user.uid}/workouts`, logToDelete.id));
    const newLogs = [...logs];
    newLogs.splice(index, 1);
    setLogs(newLogs);
  };

  const handleSetChange = (
    logIndex: number,
    exIndex: number,
    setIndex: number,
    field: string,
    value: string
  ) => {
    const newLogs = [...logs];
    const backup = JSON.parse(JSON.stringify(logs));
    setHistory((prev) => [...prev, backup]);

    const set = newLogs[logIndex].exercises[exIndex].sets[setIndex] as any;
    set[field] = parseFloat(value);
    setLogs(newLogs);
    updateWorkout(newLogs[logIndex]);
  };

  const handleDeleteSet = (
    logIndex: number,
    exIndex: number,
    setIndex: number
  ) => {
    const newLogs = [...logs];
    const backup = JSON.parse(JSON.stringify(logs));
    setHistory((prev) => [...prev, backup]);

    newLogs[logIndex].exercises[exIndex].sets.splice(setIndex, 1);
    setLogs(newLogs);
    updateWorkout(newLogs[logIndex]);
  };

  const handleDeleteExercise = (logIndex: number, exIndex: number) => {
    const newLogs = [...logs];
    const backup = JSON.parse(JSON.stringify(logs));
    setHistory((prev) => [...prev, backup]);

    newLogs[logIndex].exercises.splice(exIndex, 1);
    setLogs(newLogs);
    updateWorkout(newLogs[logIndex]);
  };

  const handleAddSet = (logIndex: number, exIndex: number) => {
    const newLogs = [...logs];
    const backup = JSON.stringify(logs);
    setHistory((prev) => [...prev, JSON.parse(backup)]);

    const mode = newLogs[logIndex].exercises[exIndex].mode;
    if (mode === "lift") {
      newLogs[logIndex].exercises[exIndex].sets.push({
        reps: 0,
        weight: 0,
        rpe: 0,
      });
    } else {
      newLogs[logIndex].exercises[exIndex].sets.push({
        distance: 0,
        pace: 0,
        time: 0,
      });
    }
    setLogs(newLogs);
    updateWorkout(newLogs[logIndex]);
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const previous = history[history.length - 1];
    setHistory((prev) => prev.slice(0, -1));
    setLogs(previous);
    previous.forEach((log) => updateWorkout(log));
  };

  if (!user)
    return <p style={{ padding: 20 }}>Please log in to edit workouts.</p>;

  return (
    <div style={{ padding: "20px", maxWidth: 500, margin: "0 auto" }}>
      <h2>Edit Workouts</h2>

      <div
        style={{
          display: "flex",
          gap: "12px",
          paddingBottom: "24px",
          justifyContent: "center",
        }}
      >
        <StyledButton onClick={handleUndo} disabled={history.length === 0}>
          ⎌ Undo
        </StyledButton>
        <StyledButton onClick={saveAllLogs}>💾 Save Changes</StyledButton>
      </div>

      {logs.map((log, i) => (
        <div key={log.id} style={{ marginBottom: 24 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3 style={{ margin: 0 }}>
              {new Date(log.createdAt.seconds * 1000).toDateString()}
            </h3>
            <button onClick={() => handleDeleteWorkout(i)}>🗑️</button>
          </div>

          {log.exercises.map((ex, exIndex) => (
            <div key={exIndex} style={{ marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <strong>{ex.name}</strong>
                <button onClick={() => handleDeleteExercise(i, exIndex)}>
                  🗑️
                </button>
              </div>

              {ex.sets.map((set, setIndex) => (
                <EditWorkoutRow
                  key={setIndex}
                  set={set}
                  mode={ex.mode}
                  onChange={(field, value) =>
                    handleSetChange(i, exIndex, setIndex, field, value)
                  }
                  onDelete={() => handleDeleteSet(i, exIndex, setIndex)}
                />
              ))}

              <StyledButton
                onClick={() => handleAddSet(i, exIndex)}
                style={{ marginTop: 8 }}
              >
                + Add Set
              </StyledButton>
            </div>
          ))}
        </div>
      ))}

      <StyledButton
        onClick={async () => {
          await saveAllLogs();
          navigate("/tracker");
        }}
        style={{ marginTop: 30 }}
      >
        ← Back to Tracker
      </StyledButton>
    </div>
  );
};

export default EditWorkoutHistory;
